import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import ImageGallery from './ImageGallery';

import laContessinaLogo from '@/assets/LA_CONTESSINA-black.webp';
import bbLogo from '@/assets/BB_black.webp';
import pataviumartLogo from '@/assets/Pataviumart.Black.webp';
import italianStoneLogo from '@/assets/Italian_Stone_Black.webp';

const brandLogos: Record<string, string> = {
  laContessina: laContessinaLogo,
  barjBuzzoni: bbLogo,
  pataviumart: pataviumartLogo,
  italianStoneSource: italianStoneLogo,
};

const brandWebsites: Record<string, string> = {
  laContessina: 'https://www.lacontessina.com',
  barjBuzzoni: 'https://www.barjbuzzoni.it',
  pataviumart: 'https://www.luxury-lighting-pataviumart.com',
  italianStoneSource: 'https://www.italianstonesource.com',
};

interface BrandSectionProps {
  id: string;
  brandKey: string;
  images: string[];
  reverse?: boolean;
  videoId?: string;
}

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.15, ease: 'easeOut' as const },
  }),
};

const BrandSection = ({ id, brandKey, images, reverse = false, videoId }: BrandSectionProps) => {
  const { t } = useTranslation();

  return (
    <section id={id} className="py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className={`mb-16 ${reverse ? 'text-right' : ''}`}
        >
          {brandLogos[brandKey] ? (
            <motion.div variants={fadeUp} custom={0} className="mb-2">
              <img src={brandLogos[brandKey]} alt={t(`brands.${brandKey}.name`)} className={`${brandKey === 'laContessina' ? 'h-20 md:h-28' : 'h-12 md:h-16'} ${reverse ? 'ml-auto' : ''}`} />
            </motion.div>
          ) : (
            <motion.h2 variants={fadeUp} custom={0} className="text-4xl md:text-6xl text-foreground mb-2 font-medium">
              {t(`brands.${brandKey}.name`)}
            </motion.h2>
          )}
          <motion.span variants={fadeUp} custom={1} className="text-xs tracking-[0.3em] uppercase text-primary block mb-6 font-medium">
            {t(`brands.${brandKey}.tagline`)}
          </motion.span>
          <motion.p variants={fadeUp} custom={2} className={`text-muted-foreground font-light leading-relaxed max-w-3xl ${reverse ? 'ml-auto' : ''}`}>
            {t(`brands.${brandKey}.description`)}
          </motion.p>
          <motion.div variants={fadeUp} custom={3} className={`mt-6 ${reverse ? 'flex justify-end' : ''}`}>
            <a
              href={brandWebsites[brandKey]}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-3 text-sm tracking-[0.1em] uppercase border border-foreground/30 text-foreground hover:border-primary hover:text-primary transition-all duration-300 rounded-full font-medium"
            >
              {t('brands.learnMore')}
            </a>
          </motion.div>
        </motion.div>

        {/* Image grid */}
        <ImageGallery
          images={images}
          alt={t(`brands.${brandKey}.name`)}
          gridClassName="mb-16"
          renderGrid={(imgs, onImageClick) => (
              <div className={`grid grid-cols-2 gap-3 auto-rows-[340px] md:auto-rows-[420px] ${reverse ? 'direction-rtl' : ''}`} style={reverse ? { direction: 'rtl' } : undefined}>
              {imgs.map((img, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.8, delay: i * 0.15 }}
                  className={`overflow-hidden rounded-lg cursor-pointer ${
                    i === 0 ? 'col-span-2 md:col-span-1 md:row-span-2' : 'col-span-1'
                  }`}
                  style={reverse ? { direction: 'ltr' } : undefined}
                  onClick={() => onImageClick(i)}
                >
                  <img
                    src={img}
                    alt={t(`brands.${brandKey}.name`)}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </motion.div>
              ))}
            </div>
          )}
        />

        {/* Highlights */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 border-t border-border pt-10"
        >
          {(['highlight1', 'highlight2', 'highlight3'] as const).map((key, i) => (
            <motion.div key={key} variants={fadeUp} custom={i} className="flex items-start gap-4">
              <span className="w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0 mt-2" />
              <span className="text-sm text-foreground/80 font-light leading-relaxed">
                {t(`brands.${brandKey}.${key}`)}
              </span>
            </motion.div>
          ))}
        </motion.div>

        {/* Video player */}
        {videoId && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8 }}
            className="mt-16"
          >
            <div className={`mb-8 max-w-3xl ${reverse ? 'ml-auto text-right' : ''}`}>
              <span className="text-xs tracking-[0.3em] uppercase text-primary block mb-3 font-medium">
                {t(`brands.${brandKey}.videoLabel`)}
              </span>
              <h3 className="text-2xl md:text-3xl text-foreground mb-4 font-medium">
                {t(`brands.${brandKey}.videoTitle`)}
              </h3>
              <p className="text-muted-foreground font-light leading-relaxed">
                {t(`brands.${brandKey}.videoDescription`)}
              </p>
            </div>
            <div className="overflow-hidden rounded-lg shadow-2xl">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={`https://www.youtube.com/embed/${videoId}`}
                  title={t(`brands.${brandKey}.videoTitle`)}
                  className="absolute inset-0 w-full h-full"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default BrandSection;
